import {createContext, useContext, useState, useEffect, useRef} from 'react';
import {useAppKit, useAppKitAccount, useAppKitNetwork, useAppKitState, useDisconnect} from "@reown/appkit/react";
import {useAppKitSIWX} from "@reown/appkit-siwx/react";
import {SIWXSession} from "@reown/appkit-siwx";
import {ZenexAuthentication} from "@/ZenexAuthentication.ts";
import {useStableCallback} from "@tanstack/react-router";
import {setCardsSession} from "@/api/cardsApi.ts";

type ReownContextType = {
  address: string | null;
  token: string | null;

  isConnected: boolean;
  state: string ;

  isModalOpen: boolean;

  modalOpen: () => Promise<void>;
  modalClose: () => Promise<void>;
  disconnect: () => void;
};


type SessionContextType = SIWXSession | undefined


const ReownContext = createContext<ReownContextType | undefined>(undefined);

export const ReownProvider = ({children}: { children: React.ReactNode }) => {


  const siwx = useAppKitSIWX<ZenexAuthentication>()

  const [siwxSession, setSiwxSession] = useState<SessionContextType>(undefined)
  const [token, setToken] = useState<string|null>(null)

  useEffect(() => {
    if (!siwx) return

    const unsubscribe = siwx.on('sessionChanged', session => {
      // console.log('za.sessionChanged.Event',session)
      setSiwxSession(session)
    })

    return () => {
      unsubscribe()
    }
  }, [siwx])


  const {status, embeddedWalletInfo, isConnected, address} = useAppKitAccount();
  // const {status, embeddedWalletInfo, isConnected, address} = useAppKitAccount();


  // Syncing your UI with the modal’s open/closed state
  // Tracking which network the user has selected
  // Creating custom UI elements that respond to modal state changes
  // Implementing custom loading states based on modal state
  // Checking if AppKit has been fully initialized before rendering components
  // Displaying chain-specific UI based on the active blockchain namespace
  const {
    initialized, // initialized: Boolean that indicates if AppKit has been initialized. This sets to true when all controllers, adapters and internal state is ready
    loading: isModalLoading, //   loading: Boolean that indicates if AppKit is loading
    open: isModalOpen , // open: Boolean that indicates if the modal is open
    selectedNetworkId, // open: Boolean that indicates if the modal is open
    activeChain // activeChain: The active chain namespace (e.g., ‘eip155’, ‘solana’, ‘bip122’)
  } = useAppKitState();

  const [authenticatedAddress, setAuthenticatedAddress] = useState<string | null>(null);

  useEffect(() => {
    if (address && address === siwxSession?.data.accountAddress) {
      const sessionJson = JSON.stringify(siwxSession);
      setToken(sessionJson);
      setCardsSession({
        walletAddress: address,
        signatureBase64: btoa(sessionJson),
      });
      setAuthenticatedAddress(address);
    } else {
      setToken(null);
      setCardsSession(null);
      setAuthenticatedAddress(null);
    }
  }, [address, siwxSession?.data.accountAddress, siwxSession]);

  const [reownState, setReownState] = useState("initializing");

  const {open: modalOpen, close : modalClose} = useAppKit()
  const {disconnect} = useDisconnect()


  const isClickProcessing = useRef(false);

  const safeModalOpen = useStableCallback(async () => {
    // 1. Если SDK не готов, окно уже открыто или мы в процессе клика — выходим
    if (!initialized || isModalOpen || isClickProcessing.current || isModalLoading) {
      return;
    }

    isClickProcessing.current = true;

    try {
      await modalOpen({view: "Connect", namespace: "solana"});
    } catch (error) {
      console.error("AppKit open error:", error);
    } finally {
      // Снимаем блокировку через секунду, когда Reown точно обновит стейт isModalOpen
      setTimeout(() => {
        isClickProcessing.current = false;
      }, 1000);
    }
  });


  useEffect(() => {
    // console.log("appKit update","AppKitState.initialized", initialized);
    if (initialized && reownState === "initializing") {
      setReownState('initialized')
    }
  },[initialized])



  // Displaying the connected wallet address in your UI
  // Checking if a user is connected before showing certain features
  // Getting user information for embedded wallets
  // Handling multi-chain scenarios where you need account info for specific chains
  // Related hooks: useAppKitWallet, useDisconnect
  // Hook for accessing account data and connection status for each namespace when working in a multi-chain environment.
  // const { address, isConnected, caipAddress, status, embeddedWalletInfo } =
  //   useAppKitAccount();


  // useEffect(() => {
  //   console.log("appKit update","AppKitAccount.address", address);
  // },[address])
  //
  // useEffect(() => {
  //   console.log("appKit update","AppKitAccount.isConnected", isConnected);
  // },[isConnected])

  // useEffect(() => {
  //   console.log("appKit update","AppKitAccount.caipAddress", caipAddress);
  // },[caipAddress])

  // useEffect(() => {
  //   console.log("appKit update","AppKitAccount.status", status);
  // },[status])
  //
  // useEffect(() => {
  //   console.log("appKit update","AppKitAccount.embeddedWalletInfo", embeddedWalletInfo);
  // },[embeddedWalletInfo])





  // Displaying the current network/chain in your UI
  // Switching networks when a user selects a different chain
  // Validating if a user is on the correct network for your dApp
  // Handling network-specific features or contracts
  // Related hooks: useAppKitBalance, useWalletInfo
  const { caipNetwork, caipNetworkId, chainId, switchNetwork } = useAppKitNetwork()


  // useEffect(() => {
  //   console.log("appKit update","AppKitNetwork.caipNetwork", caipNetwork);
  // },[caipNetwork])
  //
  // useEffect(() => {
  //   console.log("appKit update","AppKitNetwork.caipNetworkId", caipNetworkId);
  // },[caipNetworkId])
  //
  // useEffect(() => {
  //   console.log("appKit update","AppKitNetwork.chainId", chainId);
  // },[chainId])
  //
  // useEffect(() => {
  //   console.log("appKit update","AppKitNetwork.switchNetwork", switchNetwork);
  // },[switchNetwork])







  // useEffect(() => {
  //   console.log("appKit update","AppKitState.loading", isModalLoading);
  // },[isModalLoading])
  //
  // useEffect(() => {
  //   console.log("appKit update","AppKitState.open", isModalOpen);
  // },[isModalOpen])
  //
  // useEffect(() => {
  //   console.log("appKit update","AppKitState.selectedNetworkId", selectedNetworkId);
  // },[selectedNetworkId])
  //
  // useEffect(() => {
  //   console.log("appKit update","AppKitState.activeChain", activeChain);
  // },[activeChain])


  return (
    <ReownContext.Provider value={{
      address: authenticatedAddress,
      token: token,
      state: reownState,
      isConnected:isConnected,
      modalOpen:safeModalOpen,
      modalClose:modalClose,
      disconnect,
      isModalOpen
    }}>
      {children}
    </ReownContext.Provider>
  );
};

export const useReown = () => {
  const ctx = useContext(ReownContext);
  if (!ctx) throw new Error('useAuth must be used within ReownProvider');
  return ctx;
};
